import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import Cookies from 'js-cookie';
import { lazy, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Alert from '~/components/Alert';
import FileField from '~/components/forms/FileField';
import RadioGroupField from '~/components/forms/RadioGroupField';
import TextAreaField from '~/components/forms/TextAreaField';
import { FormAction, FormBody } from '~/components/FormUtils';
import PageHeader from '~/components/PageHeader';
import Button from '~/components/ui/Button';
import Icon from '~/components/ui/Icon';
import Stack from '~/components/ui/Stack';
import { cn } from '~/lib/utils';
import { AUTH_COOKIE_NAME } from '~/modules/auth/lib';
import { useAuthorizationStatus } from '~/modules/auth/useAuthorizationStatus';
import { FeedbackType, feedbackTypeOptions } from '~/modules/support/types';
import { useSubmitFeedback } from '~/modules/support/useSubmitFeedback';

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

type RecaptchaRefType = {
  executeAsync: () => Promise<string>;
};

type FormValues = {
  type: FeedbackType;
  text: string;
  attachments?: File[];
};

const schema = Joi.object({
  type: Joi.string().valid('bug_report', 'feature_request', 'other').required(),
  text: Joi.string().required().label('Message'),
  attachments: Joi.array().optional(),
});

type ThumbnailsProps = { files: File[]; onRemove: (index: number) => void };

function Thumbnails({ files, onRemove }: ThumbnailsProps) {
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);

  useEffect(() => {
    return () => {
      for (const u of urls) URL.revokeObjectURL(u);
    };
  }, [urls]);

  return (
    <div className="flex flex-wrap gap-3">
      {files.map((file, index) => (
        <div key={index} className="relative group rounded-md overflow-hidden aspect-square">
          <img src={urls[index]} alt={file.name} className="size-25 object-cover" />
          <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-xs p-1 truncate text-shadow-2xs">
            {file.name}
          </div>
          <Button
            variant="icon"
            size="xs"
            onClick={(event) => {
              event.stopPropagation();
              onRemove(index);
            }}
            className="absolute px-0 top-1 right-1 size-6 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
          >
            <Icon name="close" className="text-[16px]" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export default function Support() {
  const { t } = useTranslation();
  const recaptchaRef = useRef(null);

  const { mutateAsync: addFeedback, isError } = useSubmitFeedback();

  const { isAuthorized } = useAuthorizationStatus();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset,
  } = useForm<FormValues>({
    resolver: joiResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      type: 'bug_report',
      text: '',
      attachments: [],
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    const authToken = Cookies.get(AUTH_COOKIE_NAME);
    if (!authToken) {
      const captchaToken = await (recaptchaRef.current as RecaptchaRefType | null)?.executeAsync();
      localStorage.setItem('captchaToken', captchaToken ?? '');
    }

    const attachments = (data.attachments ?? []).filter(Boolean);

    await addFeedback({
      application_id: crypto.randomUUID(),
      text: data.text,
      type: data.type,
      attachments,
    });

    reset({
      type: 'bug_report',
      text: '',
      attachments: [],
    });
  });

  return (
    <Stack
      col
      gap={6}
      className="flex-1 compact:gap-5 compact:[&_textarea]:h-33 compact:[&_textarea]:min-h-33 compact:[&_textarea]:p-3 compact:[&_textarea]:text-base"
    >
      <PageHeader id="support" />
      {isError && <Alert variant="error" description={t('support.error')} />}

      <form onSubmit={onSubmit}>
        <FormBody>
          <fieldset className="m-0 border-0 p-0">
            <legend className="mb-4 text-sm text-section-title">
              {t('support.chooseDepartment')}
            </legend>
            <RadioGroupField
              radioLabel={t('support.feedbackType')}
              name="type"
              control={control}
              options={feedbackTypeOptions}
              isRequired
            />
          </fieldset>

          <TextAreaField
            control={control}
            name="text"
            label={t('support.message')}
            className="w-full"
            maxLength={32767}
          />

          <FileField control={control} name="attachments" multiple maxFiles={5} className="w-full">
            {({ files, onSelect, onRemove, canAddMore, isDragOver }) => (
              <div
                className={cn(
                  'flex min-h-55 w-full flex-col gap-4 rounded-2xl border border-dashed p-8 text-center compact:min-h-0 compact:p-4 compact:gap-2.5 compact:[&>button]:w-full',
                  files.length === 0 ? 'items-center' : 'items-start',
                  isDragOver
                    ? 'border-attachment-border-focused bg-attachment-background-over'
                    : 'border-attachment-border bg-attachment-background',
                  canAddMore && 'cursor-pointer',
                )}
                onClick={canAddMore ? onSelect : undefined}
              >
                {files.length === 0 ? (
                  <>
                    <span className="grid size-13 place-items-center rounded-full bg-icon-background compact:hidden">
                      <Icon name="upload" className="text-[26px] text-icon" />
                    </span>
                    <p className="text-sm leading-[1.8] text-body compact:hidden">
                      {t('support.fileUpload.dragDrop')}
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect();
                      }}
                    >
                      {t('support.fileUpload.chooseImage')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Thumbnails files={files} onRemove={onRemove} />
                    {canAddMore && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="min"
                        onClick={(event) => {
                          event.stopPropagation();
                          onSelect();
                        }}
                        className="mt-auto self-end text-sm text-link"
                      >
                        {t('support.fileUpload.chooseImage')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </FileField>
        </FormBody>
        {!isAuthorized && (
          <ReCAPTCHA
            sitekey={window.__CONFIG.VITE_RECAPTCHA_SITE_KEY}
            size="invisible"
            ref={recaptchaRef}
          />
        )}
        <FormAction className="compact:[&>button]:w-auto compact:[&>button]:min-w-0">
          <Button type="submit" isPending={isSubmitting} isDisabled={!isValid}>
            {t('support.submit')}
          </Button>
        </FormAction>
      </form>
    </Stack>
  );
}
